// app services
import { FormControl } from '@angular/forms';
import { ZoomService } from '../../../ui-testrunner/zoom.service';
import { LangService } from '../../../core/lang.service';
import { WhitelabelService } from '../../../domain/whitelabel.service';
import { ITestDesignPayload } from '../../../ui-testtaker/view-tt-test-runner/view-tt-test-runner.component';
import { IPanelModulesConfig } from '../models/assessment-framework';
import { updateQChangeCounter } from '../models/expected-answer';
import { ISampleTestForm } from '../models/types';
import { ItemBankCtrl } from './item-bank';
import { ItemDiffCtrl } from './item-diff';
import { ItemFilterCtrl } from './item-filter';
import { ItemBankSaveLoadCtrl } from './save-load';
import { TestFormGen } from './testform-gen';
import { Destroyable } from './destroyable';
import { ItemSetPublishingCtrl } from './publishing';
import { buildOssltJourneyModules, checkHasMapModule } from '../../../ui-student/osslt-page-switcher/data/osslt-journey-modules';
import {
  filterSectionIndexesByOssltSession,
  getOssltMapSession,
  getPanelSectionIndexes,
  IModuleMapPanelOption,
  OssltMapSessionLetter,
} from '../../widget-osslt-module-map/module-map-panel-scope';

export class ItemSetPreviewCtrl implements Destroyable {

  questionTextSize = new FormControl('1.5');
  sampleTestForm: ISampleTestForm;
  isTestFormPrintView:boolean;
  isShowingResults: boolean = false;
  currentTestFormId:string;
  isTesletPreview = false;
  /**
   * Live OSSLT student-map preview. When set, the editor hides the authoring
   * chrome and shows `osslt-assessment` (or a module's test-runner) using the
   * in-memory panel form — not the anonymous sample API.
   */
  ossltJourney: {
    sampleTestForm: ISampleTestForm;
    modules: any[];
    sectionsAllowed: number[];
    panelSectionIndexes: number[];
    session: { id: number; label: string; letter: OssltMapSessionLetter; sections_allowed: number[] };
    currentModuleIndex: number | null;
    testRunnerIndex: number | null;
    mapKey: string;
  } | null = null;
  /** Stable one-element list so `*ngFor` remounts `osslt-assessment` on session change. */
  ossltMapInstanceKeys: string[] = [];

  constructor(
    public whitelabel: WhitelabelService,
    public lang: LangService,
    public testFormGen: TestFormGen,
    public itemBankCtrl: ItemBankCtrl,
    public saveLoadCtrl: ItemBankSaveLoadCtrl,
    public zoom: ZoomService,
    public itemDiffCtrl: ItemDiffCtrl,
    public itemFilterCtrl: ItemFilterCtrl,
    public publishingCtrl: ItemSetPublishingCtrl
  ){
    this.questionTextSize.valueChanges.subscribe((val)=> {this.zoom.update(val, true);})
    this.zoom.update(this.questionTextSize.value, true);
  }

  destroy() {

  }

  submitTest = (skipPost?: boolean) => {
    if(!skipPost) {
      this.showResults();
    }
  }
  
  showResults = () => {
    if (this.whitelabel.getSiteFlag('IS_BCED')){
      this.isShowingResults = true;
    }
    else{
      this.exitSampleTestForm();
    }
    return Promise.resolve();
  }

  get isOssltJourneyActive(): boolean {
    return !!this.ossltJourney;
  }

  get isOssltJourneyRunnerActive(): boolean {
    return !!this.ossltJourney && this.ossltJourney.currentModuleIndex != null;
  }

  /** Angular template parser here does not accept `??`; default missing index to 0. */
  get ossltTestRunnerIndex(): number {
    const index = this.ossltJourney && this.ossltJourney.testRunnerIndex;
    return index != null ? index : 0;
  }

  exitSampleTestForm = () => {
    this.sampleTestForm = null;
    this.isShowingResults = false;
    if (this.ossltJourney) {
      this.ossltJourney.currentModuleIndex = null;
      this.ossltJourney.testRunnerIndex = null;
    }
    this.zoom.update(this.questionTextSize.value, true);
  }

  exitOssltJourney = () => {
    this.ossltJourney = null;
    this.ossltMapInstanceKeys = [];
    this.exitSampleTestForm();
  }

  returnToOssltMap = () => {
    if (!this.ossltJourney) {
      return;
    }
    this.ossltJourney.currentModuleIndex = null;
    this.ossltJourney.testRunnerIndex = null;
    this.sampleTestForm = this.ossltJourney.sampleTestForm;
  }

  enterOssltModule = (moduleIndex: number) => {
    if (!this.ossltJourney) {
      return;
    }
    const sectionsAllowed = this.ossltJourney.sectionsAllowed;
    this.ossltJourney.currentModuleIndex = moduleIndex;
    this.ossltJourney.testRunnerIndex = sectionsAllowed[moduleIndex] ?? 0;
    this.sampleTestForm = this.ossltJourney.sampleTestForm;
  }

  /**
   * Construct the selected assembled panel as a student form and open the OSSLT
   * map inside the editor. Uses TestFormGen + live item-bank questions so
   * unsaved authoring state is what the map shows.
   */
  async previewOssltJourney(
    panel: IModuleMapPanelOption,
    sessionLetter: OssltMapSessionLetter = 'A'
  ): Promise<void> {
    if (!panel) {
      return;
    }
    this.testFormGen.documentMap = new Map();
    const asmtFmrk = this.itemBankCtrl.frameworkCtrl?.asmtFmrk;
    let sampleTestForm: ISampleTestForm;
    if (panel.isLinear) {
      sampleTestForm = <any> await this.testFormGen.generateLinearTestForm();
    } else if (panel.isMsCat) {
      sampleTestForm = <any> await this.testFormGen.generateMsCatTestForm(panel.source as IPanelModulesConfig);
    } else {
      sampleTestForm = <any> await this.testFormGen.generateTloftSampleFormFromPanel(panel.source);
    }

    const panelSectionIndexes = getPanelSectionIndexes(asmtFmrk, panel);
    if (!panelSectionIndexes.length) {
      console.warn('OSSLT_JOURNEY_NO_SECTIONS', panel.id);
    }
    this.ossltJourney = {
      sampleTestForm,
      modules: [],
      sectionsAllowed: [],
      panelSectionIndexes,
      session: { id: 0, label: 'osslt_session_a', letter: 'A', sections_allowed: [] },
      currentModuleIndex: null,
      testRunnerIndex: null,
      mapKey: '',
    };
    this.sampleTestForm = sampleTestForm;
    this.currentTestFormId = panel.isLinear ? 'OSSLT-LINEAR' : `OSSLT-PANEL-${panel.id}`;
    this.isShowingResults = false;
    this.applyOssltSession(sessionLetter);
  }

  applyOssltSession(letter: OssltMapSessionLetter): void {
    if (!this.ossltJourney) {
      return;
    }
    const asmtFmrk = this.itemBankCtrl.frameworkCtrl?.asmtFmrk;
    const def = getOssltMapSession(letter, asmtFmrk);
    const sectionsAllowed = filterSectionIndexesByOssltSession(
      this.ossltJourney.panelSectionIndexes,
      letter,
      asmtFmrk,
    ).filter(section => checkHasMapModule(section, this.itemBankCtrl.frameworkCtrl?.asmtFmrk, this.ossltJourney?.sampleTestForm));
    const modules = buildOssltJourneyModules({
      asmtFmrk: this.itemBankCtrl.frameworkCtrl?.asmtFmrk,
      testForm: this.ossltJourney.sampleTestForm,
      sectionsAllowed,
      lang: this.lang.c(),
      getTranslation: (slug, _unused, params) => this.lang.tra(slug, undefined, params),
      getQuestion: (id) => this.itemBankCtrl.getQuestionById(id),
    });
    const mapKey = `${this.currentTestFormId}-${letter}`;
    this.ossltJourney = {
      ...this.ossltJourney,
      modules,
      sectionsAllowed,
      session: {
        id: def.id,
        label: def.label,
        letter: def.letter,
        sections_allowed: sectionsAllowed,
      },
      currentModuleIndex: null,
      testRunnerIndex: null,
      mapKey,
    };
    this.ossltMapInstanceKeys = [mapKey];
    this.sampleTestForm = this.ossltJourney.sampleTestForm;
    if (this.itemBankCtrl.frameworkCtrl) {
      this.itemBankCtrl.frameworkCtrl.moduleMapSession = letter;
    }
  }

  async previewTestForm(testFormId:number, testForm: ITestDesignPayload, isTestFormPrintView:boolean=false) {
    this.isTestFormPrintView = isTestFormPrintView;
    this.sampleTestForm = await this.testFormGen.convertLOFTFormDefToPreview(testForm);
    this.currentTestFormId = 'SAMPLE'+testFormId;
    // console.log('this.sampleTestForm', this.sampleTestForm)
    const questionSrcDb = [];
    this.sampleTestForm.questionSrcDb.forEach((val, key)=>{
      questionSrcDb.push({val, key});
    })
    const sampleTestFormExport = {
      ... this.sampleTestForm,
      questionSrcDb,
    }
    // downloadStr(JSON.stringify(sampleTestFormExport), 'test.json');
  }

  async previewTloft(targetPanel?:any){
    this.testFormGen.documentMap = new Map();
    const sampleTestForm:ISampleTestForm = <any> await this.testFormGen.generateTloftSampleFormFromPanel(targetPanel)
    this.sampleTestForm = <any> sampleTestForm;
  }

  async previewTestlet(testlets:number[]){ 
    this.isTesletPreview = false;
    this.testFormGen.documentMap = new Map();
    const sampleTestForm:ISampleTestForm = <any> await this.testFormGen.generateTestletForm(testlets);
    sampleTestForm.isTestletPreview = true;
    const questions = sampleTestForm.currentTestDesign.sections.filter(section => section.questions.length)[0].questions;
    sampleTestForm.currentTestDesign.sections.forEach( section => section.questions = questions);
    this.sampleTestForm = <any> sampleTestForm;
  }

  async previewMsCat(targetPanel?:IPanelModulesConfig){
    this.testFormGen.documentMap = new Map();
    const sampleTestForm:ISampleTestForm = <any> await this.testFormGen.generateMsCatTestForm(targetPanel)
    this.sampleTestForm = <any> sampleTestForm;
  }

  async previewLinearTestForm() {
    // const res = await Promise.all([this.publishingCtrl.updateCaptionVoiceovers(this.lang.c()), this.testFormGen.generateLinearTestForm()]);
    // this.sampleTestForm = res[1];
    const res = await this.testFormGen.generateLinearTestForm();
    this.sampleTestForm = res;
    // downloadStr(JSON.stringify(this.sampleTestForm), 'test.json');
  }

  setLang(langCode: string) {
    if(this.lang.c() === langCode) {
      return;
    }

    this.lang.setCurrentLanguage(langCode);
    const memQuestion = this.itemBankCtrl.currentQuestion;
    this.itemBankCtrl.currentQuestion = null;
    setTimeout(() => {
      this.itemBankCtrl.resetQuestionState();
      this.itemBankCtrl.currentQuestion = memQuestion;
      this.itemBankCtrl.refreshEditingDisabled();
      this.itemBankCtrl.refreshEditingDisabledCurrQ();
      if(this.itemBankCtrl.isPsychometricViewEnabled) {
        for(const item of this.itemBankCtrl.itemList) {
          updateQChangeCounter(item); //refreshes the parameter fields for language independent parameters in framework view
        }
        this.itemFilterCtrl.updateItemFilter(); //refreshes filtering for language independent params
      } else {
        this.itemDiffCtrl.refreshSuggestions();
      }
      // console.log('this.currentQuestion', this.currentQuestion, this.getCurrentQuestionContent());
    }, 300);
  }
  getTestTakerName = () =>  this.lang.tra('ie_student_tt_name');

  saveQuestionResponse = (data) => Promise.resolve();
  
  updateDragZoomCorrection(){
    const cdkStyle = document.getElementById('cdk-font-size-override');
    if (cdkStyle){
      cdkStyle.innerText = `.cdk-drag-preview { font-size: ${this.questionTextSize.value}em; }`
    }
  }

}